import React, { useState, FC, ReactElement } from "react";

import { useMsal } from "@azure/msal-react";

import { Grid, TextField, Tooltip, Typography } from "@mui/material";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { DeleteOutline } from "@mui/icons-material";

import apiCall from "../../utils/apiCall";
import { DELETE_USER_PROMPT, UPDATE_USER_PROMPT } from "../../endpoint";

import { useAppDispatch, useAppSelector } from "../../hooks/reduxhook";
import { addPrompts, getType } from "../../redux/slices/typeSlice";
import { addError } from "../../redux/slices/errorSlice";
import { loadingforprompts } from "../../redux/slices/loadingSlice";

import EditIcon from "@mui/icons-material/Edit";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ConfirmDeleteModal from "../ConfirmDelete";

const Prompt: FC<any> = ({ value }: { value: string }): ReactElement => {
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const { accounts } = useMsal();
  const account = accounts[0];

  const chatType = useAppSelector(getType);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const dispatch = useAppDispatch();

  const deletePrompt = () => {
    dispatch(loadingforprompts(true));
    apiCall("POST", DELETE_USER_PROMPT, {
      user_email: account.username,
      chat_type: chatType.prompt_storage,
      prompt: value,
    })
      .then((res) => {
        dispatch(addPrompts(res.result));
      })
      .catch(() => {
        dispatch(
          addError("Failed to delete the saved user query.Please try again.")
        );
      })
      .finally(() => {
        dispatch(loadingforprompts(false));
      });
  };

  const updatePrompt = () => {
    dispatch(loadingforprompts(true));
    apiCall("POST", UPDATE_USER_PROMPT, {
      old_query: value,
      new_query: editValue,
      user_email: account.username,
      chat_type: chatType.prompt_storage,
    })
      .then((res) => {
        setEditing(false);
        dispatch(addPrompts(res.result));
      })
      .catch(() => {
        dispatch(
          addError("Failed to update the saved user query.Please try again")
        );
      })
      .finally(() => {
        dispatch(loadingforprompts(false));
      });
  };

  return (
    <Grid container alignItems="center" px={2}>
      {copied ? (
        <ContentPasteIcon
          fontSize="small"
          sx={{
            cursor: "pointer",
            fontSize: "14px",
            mr: 0.2,
            color: "primary",
          }}
          aria-label="paste icon prompt"
        />
      ) : (
        <Tooltip title="Copy">
        <ContentCopyIcon
          id="copyicon"
          sx={{
            cursor: "pointer",
            fontSize: "14px",
            mr: 0.2,
            color: "primary",
          }}
          onClick={() => {
            navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => {
              setCopied(false);
            }, 2000);
          }}
          aria-label="copy icon prompt"
        />
        </Tooltip>
      )}
      {editing ? (
        <TextField
          inputProps={{ className: "savedtextfield" }}
          value={editValue}
          onChange={(e) => {
            setEditValue(e.target.value);
          }}
          size="small"
          multiline
          maxRows={3}
          minRows={1}
          onKeyDown={(e) => {
            if (e.key === "Enter") updatePrompt();
          }}
          onBlur={() => {
            updatePrompt();
          }}
          sx={{
            flex: 1,
          }}
        />
      ) : editValue?.length > 17 ? (
        <Tooltip title={editValue} className="firstlettercapital">
          <Typography
            sx={{
              // p: 1,
              flex: 1,
              wordBreak: "break-word",
            }}
            aria-label={editValue}
          >
            {editValue?.substring(0, 15) + "..."}
          </Typography>
        </Tooltip>
      ) : (
        <Typography
          sx={{
            // p: 1,
            flex: 1,
            wordBreak: "break-word",
          }}
          aria-label={editValue}
        >
          {editValue}
        </Typography>
      )}
      <Grid>
        {editing ? (
          <TaskAltIcon
            color="success"
            fontSize="small"
            onClick={() => updatePrompt()}
            sx={{
              mt: 0.4,
              cursor: "pointer",
            }}
            aria-label="done icon prompt"
          />
        ) : (
          <Tooltip title="Edit">
          <EditIcon
            id="savedediticon"
            color="primary"
            fontSize="small"
            onClick={() => setEditing(true)}
            sx={{
              mt: 0.4,
              cursor: "pointer",
            }}
            aria-label="edit icon prompt"
          />
          </Tooltip>
        )}
        <Tooltip title="Delete">
        <DeleteOutline
          id="saveddeleteicon"
          color="primary"
          onClick={() => setShowConfirmDelete(true)}
          sx={{
            cursor: "pointer",
            fontSize: "18px",
          }}
          aria-label="delete icon prompt"
        />
        </Tooltip>
      </Grid>
      <ConfirmDeleteModal
        show={showConfirmDelete}
        cancel={() => setShowConfirmDelete(false)}
        confirm={() => deletePrompt()}
      />
    </Grid>
  );
};

export default Prompt;
